import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

// TokenEventManager機能を直接埋め込み（Vercel対応）
class TokenEventManager {
  constructor() {
    this.listeners = [];
  }

  addMiningListener(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(
        (listener) => listener !== callback
      );
    };
  }
}

const tokenEventManager = new TokenEventManager();

// API URL設定を直接埋め込み（Vercel対応）
const getApiUrl = () => {
  const RAILWAY_API = import.meta.env.VITE_API_URL || 'https://registration-app-production.up.railway.app';
  const LOCALHOST_API = 'http://localhost:3000';
  
  const isLocalhost = 
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1';
  
  return isLocalhost ? LOCALHOST_API : RAILWAY_API;
};
const API_URL = getApiUrl();

export const useTokenBalance = () => {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [miningEffect, setMiningEffect] = useState(null);
  const prevTokensRef = useRef([]);

  // fetchTokenBalance関数をuseRefで管理（依存配列の問題を回避）
  const fetchTokenBalance = useRef();

  fetchTokenBalance.current = async () => {
    try {
      const token = localStorage.getItem('token');

      if (!token) {
        // 認証トークンが見つかりません
        setTokens([
          { token_symbol: 'SHINKETSU', amount: 0, name: 'SHINKETSU Token' },
        ]);
        setLoading(false);
        return;
      }

      // JWTトークンから権限レベルをチェック（localStorageのuserは使わない）
      let shouldSkipAPI = false;

      try {
        // JWTトークンが適切な形式かチェック
        const tokenParts = token.split('.');
        if (tokenParts.length !== 3) {
          shouldSkipAPI = true;
        } else {
          // Base64URLをBase64に変換してからデコード
          const base64 = tokenParts[1].replace(/-/g, '+').replace(/_/g, '/');
          // パディングを追加
          const padding = base64.length % 4;
          const paddedBase64 = padding
            ? base64 + '='.repeat(4 - padding)
            : base64;

          const decoded = JSON.parse(window.atob(paddedBase64));
          if (
            decoded.isGuest ||
            decoded.permission_level < 3 ||
            !decoded.permission_level
          ) {
            shouldSkipAPI = true;
          }
        }
      } catch (jwtError) {
        // JWT解析エラーの場合はAPIをスキップ（安全側に倒す）
        console.warn('JWT decode warning in useTokenBalance:', jwtError);
        shouldSkipAPI = true;
      }

      if (shouldSkipAPI) {
        // 権限不足またはゲストユーザーの場合は0を返す
        setTokens([
          { token_symbol: 'SHINKETSU', amount: 0, name: 'SHINKETSU Token' },
        ]);
        setLoading(false);
        return;
      }

      // ウォレット残高取得APIを使用
      const response = await axios.get(`${API_URL}/api/wallets/balance`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // API Response received

      // APIレスポンス形式: { balance: number, token_balances: { QT: number, DT: number, ... } }
      const { balance, token_balances } = response.data;

      // TokenDisplayが期待する形式に変換
      const tokensData = [];
      
      // balanceは全トークンの合計値（8種 + TS）
      if (balance !== undefined) {
        tokensData.push({
          token_symbol: 'SHINKETSU',
          amount: parseFloat(balance),
          name: 'Total Balance',
        });
      }

      prevTokensRef.current = tokensData;
      setTokens(tokensData);
      setLoading(false);
    } catch (error) {
      // 401エラーの場合は静かに0を返す（エラーログを出さない）
      if (error.response?.status === 401) {
        setTokens([
          { token_symbol: 'SHINKETSU', amount: 0, name: 'SHINKETSU Token' },
        ]);
        setLoading(false);
        return;
      }
      
      // その他のエラーの場合のみログ出力
      console.warn('Token balance fetch error:', error.message);
      setTokens([
        { token_symbol: 'SHINKETSU', amount: 0, name: 'SHINKETSU Token' },
      ]);
      setLoading(false);
      return;
      /*
      try {
        const token = localStorage.getItem('token');
        const walletResponse = await axios.get(`${API_URL}/api/user/${userId}/wallet/assets`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Wallet Assets Response received
        
        if (walletResponse.data && walletResponse.data.length > 0) {
          const formattedTokens = walletResponse.data.map(item => ({
            token_symbol: item.token_symbol || 'SHINKETSU',
            amount: parseFloat(item.amount || 0),
            name: `${item.token_symbol || 'SHINKETSU'} Token`
          }));
          
          prevTokensRef.current = formattedTokens;
          setTokens(formattedTokens);
        } else {
          // フォールバックとして0を設定
          setTokens([{ token_symbol: 'SHINKETSU', amount: 0, name: 'SHINKETSU Token' }]);
        }
      } catch (fallbackError) {
        // 代替API取得エラー
        // 最終的なフォールバック
        setTokens([{ token_symbol: 'SHINKETSU', amount: 0, name: 'SHINKETSU Token' }]);
      }
      setLoading(false);
      */
    }
  };

  // マイニングエフェクト表示関数
  const showMiningEffect = useCallback(
    (tokenSymbol, amount, source = 'mining', isConsumption = false) => {
      const effectData = {
        id: Date.now(),
        tokenSymbol,
        amount: amount,
        display: isConsumption
          ? `-${Math.abs(Math.round(amount * 100) / 100)}`
          : `+${Math.round(amount * 100) / 100}`,
        isConsumption,
        source,
      };

      setMiningEffect(effectData);
    },
    []
  );

  // 手動でマイニングエフェクトをトリガーする関数
  const triggerMiningEffect = useCallback(
    (tokenSymbol, amount) => {
      showMiningEffect(tokenSymbol, amount);
    },
    [showMiningEffect]
  );

  const dismissMiningEffect = useCallback(() => {
    setMiningEffect(null);
  }, []);

  // 初期化とイベントリスナー設定
  useEffect(() => {
    // トークンがある場合のみ初回取得
    const token = localStorage.getItem('token');
    if (token) {
      fetchTokenBalance.current();
    }

    // ウォレット残高更新イベントリスナー（需要ステーク時など）
    const handleWalletBalanceUpdate = () => {
      fetchTokenBalance.current();
    };
    window.addEventListener('walletBalanceUpdate', handleWalletBalanceUpdate);

    // マイニングイベントリスナー（カスタムイベント経由）
    const handleTokenMined = (event) => {
      const eventData = event.detail;
      const effectWithIcon = {
        ...eventData,
        display: eventData.isConsumption
          ? `-${Math.abs(Math.round(eventData.amount * 100) / 100)}`
          : `+${Math.round(eventData.amount * 100) / 100}`,
      };

      setMiningEffect(effectWithIcon);

      // マイニング成功時にトークン残高を再取得（1秒後に実行してエフェクトと同期）
      if (!eventData.isConsumption && eventData.amount > 0) {
        setTimeout(() => {
          fetchTokenBalance.current();
        }, 1000);
      }
    };
    window.addEventListener('tokenMined', handleTokenMined);

    // 旧TokenEventManager互換（存在する場合のみ）
    const cleanupListener = tokenEventManager.addMiningListener((eventData) => {
      const effectWithIcon = {
        ...eventData,
        display: eventData.isConsumption
          ? `-${Math.abs(Math.round(eventData.amount * 100) / 100)}`
          : `+${Math.round(eventData.amount * 100) / 100}`,
      };

      setMiningEffect(effectWithIcon);

      if (!eventData.isConsumption && eventData.amount > 0) {
        setTimeout(() => {
          fetchTokenBalance.current();
        }, 1000);
      }
    });

    return () => {
      window.removeEventListener('walletBalanceUpdate', handleWalletBalanceUpdate);
      window.removeEventListener('tokenMined', handleTokenMined);
      cleanupListener();
    };
  }, []);

  // SHINKETSUトークンの残高を取得（安全にチェック）
  const shinketsuToken = Array.isArray(tokens)
    ? tokens.find((t) => t.token_symbol === 'SHINKETSU')
    : null;
  const shinketsuAmount = shinketsuToken
    ? parseFloat(shinketsuToken.amount)
    : 0;

  return {
    tokens,
    shinketsuAmount,
    loading,
    miningEffect,
    triggerMiningEffect,
    dismissMiningEffect,
    refreshBalance: () => fetchTokenBalance.current(),
  };
};
